import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, LoaderCircle } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { getLocalizedError } from "@/lib/api";
import { exportCsv, fileStamp, type CsvColumn } from "@/lib/exportCsv";
import { exportXlsx } from "@/lib/exportXlsx";
import { collectExportRows, exportErrorKey, type PagedResponse } from "@/lib/exportRows";

/**
 * Nút Xuất dùng chung cho mọi màn hình danh sách.
 *
 * MỘT MÀN HÌNH = MỘT BỘ CỘT. Trước đây trang PI có hai nút xuất cạnh nhau cho
 * ra hai tệp KHÁC CẤU TRÚC — "trang này" có Ngày và Trạng thái nhưng không có
 * tiền, "toàn bộ đã lọc" thì ngược lại. Hai tệp từ cùng một màn hình không dán
 * chung một sổ được. Ở đây phạm vi chỉ đổi SỐ DÒNG, không bao giờ đổi cột.
 *
 * QUYỀN: không có "quyền xuất" riêng, và đó là cố ý. Tệp dựng trong trình
 * duyệt từ chính dữ liệu máy chủ đã lọc theo menu và theo phạm vi khách hàng
 * của tài khoản. `fetchPage` phải gọi ĐÚNG endpoint với ĐÚNG bộ lọc đang mở —
 * đi vòng sang endpoint rộng hơn để "lấy cho đủ" là đi vòng qua hàng rào
 * quyền. Cột nào màn hình giấu (giá vốn, nhà cung cấp) thì cũng không được
 * truyền vào `columns`.
 *
 * ĐỊNH DẠNG: Excel (.xlsx) hoặc CSV, cùng một bộ cột, cùng số dòng — chỉ khác
 * vỏ tệp. Excel là mặc định từ 16/09/2026 (chủ dự án: "xuất dữ liệu phải có
 * Excel"); lựa chọn được nhớ trên máy người dùng vì ai đã chọn CSV một lần
 * thường là đang nạp vào phần mềm khác và sẽ chọn lại y như thế.
 */
export interface ExportButtonProps<T, R = T> {
  /** Tên tài nguyên trong tên tệp: "customers" → customers-page-2026-09-13.xlsx */
  name: string;
  /** Tên màn hình, hiện trên tiêu đề hộp thoại. */
  title: string;
  /**
   * Cột của TỆP. Khi có `expand`, một bản ghi có thể thành nhiều dòng tệp và
   * cột mô tả DÒNG chứ không phải bản ghi.
   */
  columns: CsvColumn<R>[];
  /** Các dòng đang hiển thị trên màn hình. */
  rows: readonly T[];
  /** Tổng số dòng khớp bộ lọc. Thiếu thì chỉ xuất được trang hiện tại. */
  total?: number;
  /** Nạp một trang bất kỳ của ĐÚNG bộ lọc đang mở. */
  fetchPage?: (page: number, pageSize: number, signal: AbortSignal) => Promise<PagedResponse<T>>;
  disabled?: boolean;
  /** Khóa i18n cho lỗi mạng của màn hình này. */
  errorKey?: string;
  /** Khóa nhận dạng dòng khi bản ghi không có trường `id`. */
  rowKey?: (row: T) => string;
  /** Số dòng mỗi trang mà endpoint thực sự trả về. Mặc định `EXPORT_PAGE_SIZE`. */
  pageSize?: number;
  /**
   * Biến một bản ghi thành một hoặc nhiều DÒNG của tệp.
   *
   * Sổ cái và phiếu xuất kho là ví dụ: màn hình hiện một dòng cho cả chứng từ,
   * nhưng tệp phải ra một dòng cho MỖI bút toán / MỖI dòng hàng, vì đó là dạng
   * cộng được theo tài khoản và theo LOT. Mặc định là một-một.
   */
  expand?: (row: T) => R[];
  /**
   * Nạp lượt hai và đối chiếu từng dòng KỂ CẢ khi kết quả gọn trong một trang.
   * Mặc định chỉ đối chiếu khi trải nhiều trang (xem `collectExportRows`).
   * Danh mục sản phẩm bật cờ này: tệp đó đem đi báo giá cho khách.
   */
  verify?: boolean;
}

type Scope = "page" | "filtered";
type Format = "xlsx" | "csv";

const FORMAT_KEY = "hb.export.format";

/** Định dạng đã chọn lần trước trên máy này; localStorage có thể vắng hoặc ném lỗi (chế độ riêng tư) — khi đó là Excel. */
function rememberedFormat(): Format {
  try {
    return localStorage.getItem(FORMAT_KEY) === "csv" ? "csv" : "xlsx";
  } catch {
    return "xlsx";
  }
}

function rememberFormat(format: Format): void {
  try {
    localStorage.setItem(FORMAT_KEY, format);
  } catch {
    // Không nhớ được thì lần sau lại là Excel — không đáng báo lỗi.
  }
}

export function ExportButton<T, R = T>({
  name,
  title,
  columns,
  rows,
  total,
  fetchPage,
  disabled,
  errorKey = "dataExport.failed",
  rowKey,
  pageSize,
  expand,
  verify,
}: ExportButtonProps<T, R>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        disabled={disabled || !rows.length}
        onClick={() => setOpen(true)}
      >
        <Download className="size-4" aria-hidden="true" />
        {t("common.export")}
      </Button>
      {open && (
        <ExportDialog
          name={name}
          title={title}
          columns={columns}
          rows={rows}
          total={total}
          fetchPage={fetchPage}
          errorKey={errorKey}
          rowKey={rowKey}
          pageSize={pageSize}
          expand={expand}
          verify={verify}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function ExportDialog<T, R>({
  name,
  title,
  columns,
  rows,
  total,
  fetchPage,
  errorKey,
  rowKey,
  pageSize,
  expand,
  verify,
  onClose,
}: Omit<ExportButtonProps<T, R>, "disabled"> & { errorKey: string; onClose: () => void }) {
  const { t } = useTranslation();
  const canExportAll = Boolean(fetchPage) && total !== undefined && total > rows.length;
  const [scope, setScope] = useState<Scope>(canExportAll ? "filtered" : "page");
  const [format, setFormat] = useState<Format>(rememberedFormat);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState({ done: 0, total: total ?? rows.length, verifying: false });
  const abortRef = useRef<AbortController | null>(null);
  // Rời hộp thoại giữa chừng phải hủy các vòng gọi đang chạy, không để chúng
  // chạy nốt vô ích rồi gọi setState trên một thành phần đã tháo.
  useEffect(() => () => abortRef.current?.abort(), []);

  const close = () => {
    abortRef.current?.abort();
    onClose();
  };

  const run = async () => {
    setBusy(true);
    setError(null);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const data =
        scope === "page" || !fetchPage || total === undefined
          ? [...rows]
          : await collectExportRows<T>({
              expectedTotal: total,
              signal: controller.signal,
              keyOf: rowKey,
              pageSize,
              verify,
              onProgress: (done, count, verifying) => setProgress({ done, total: count, verifying }),
              fetchPage: (page, pageSize) => fetchPage(page, pageSize, controller.signal),
            });
      if (controller.signal.aborted) return;
      const lines = expand ? data.flatMap(expand) : (data as unknown as R[]);
      const base = `${name}-${scope}-${fileStamp()}`;
      if (format === "xlsx") await exportXlsx(base, title, columns, lines);
      else exportCsv(base, columns, lines);
      rememberFormat(format);
      onClose();
    } catch (caught) {
      if (controller.signal.aborted) return;
      const known = exportErrorKey(caught);
      setError(known ? t(known) : getLocalizedError(caught, t, errorKey));
    } finally {
      if (!controller.signal.aborted) setBusy(false);
    }
  };

  return (
    <Modal
      title={`${t("dataExport.title")} · ${title}`}
      closeLabel={t("common.cancel")}
      onClose={close}
      className="max-w-lg"
    >
      <div className="space-y-4 overflow-y-auto p-5" aria-busy={busy}>
        {/*
          Chỉ hiện lựa chọn phạm vi khi THẬT SỰ có hai phạm vi. Một nhóm radio
          duy nhất một mục là câu hỏi không có câu trả lời nào khác — nó bắt
          người dùng đọc và cân nhắc một quyết định không tồn tại.
        */}
        {canExportAll ? (
          <fieldset disabled={busy} className="space-y-2">
            <legend className="mb-2 text-sm font-medium">{t("dataExport.scope")}</legend>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="export-scope"
                checked={scope === "page"}
                onChange={() => setScope("page")}
              />
              {t("dataExport.page", { count: rows.length })}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="export-scope"
                checked={scope === "filtered"}
                onChange={() => setScope("filtered")}
              />
              {t("dataExport.filtered", { count: total })}
            </label>
          </fieldset>
        ) : (
          <p className="text-sm font-medium">{t("dataExport.page", { count: rows.length })}</p>
        )}
        <fieldset disabled={busy} className="space-y-2">
          <legend className="mb-2 text-sm font-medium">{t("dataExport.format")}</legend>
          {(["xlsx", "csv"] as const).map((option) => (
            <label key={option} className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="export-format"
                className="mt-1"
                checked={format === option}
                onChange={() => setFormat(option)}
              />
              <span>
                {t(`dataExport.${option}`)}
                <span className="block text-xs text-ink-soft">{t(`dataExport.${option}Hint`)}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <p className="text-xs text-ink-soft">{t("dataExport.note")}</p>
        {busy && (
          <p role="status" className="text-sm tabular">
            {t(progress.verifying ? "dataExport.verifying" : "dataExport.preparing", {
              count: progress.done,
              total: progress.total,
            })}
          </p>
        )}
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={close} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button disabled={busy} onClick={() => void run()}>
            {busy && <LoaderCircle className="size-4 animate-spin" aria-hidden="true" />}
            {t("dataExport.download", { count: scope === "page" ? rows.length : (total ?? rows.length) })}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
